import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { AssetName } from '../../hooks/useAssetNames'
import { presentationSlides } from '../../hooks/usePresenter'
import { STAGE_HEIGHT, STAGE_WIDTH } from '../../layouts'
import * as presentationService from '../../services/presentationService'
import type { EntityRef, SharedPresentation } from '../../types/presentation.types'
import { PresentedSlideView } from './PresentedSlideView'

/**
 * Every slide of a snapshot, one per page, for the server's PDF export
 * (MYD-510): `/share/presentation/<print token>?print=1`.
 *
 * Puppeteer opens this page and waits for `window.__PRINT_READY__`, set once
 * every image has loaded (or failed) and the fonts are in, so no slide is
 * captured half-drawn. A failure sets `window.__PRINT_ERROR__` instead, so the
 * export fails at once rather than timing out.
 *
 * Each page is exactly the 1280x720 stage, through the same PresentedSlideView
 * the presenter draws with: story dividers, continuation slides and all. Maps
 * and charts are the snapshot's captured images; text stays text, so the PDF
 * is searchable and selectable.
 */
declare global {
	interface Window {
		__PRINT_READY__?: boolean
		__PRINT_ERROR__?: string
	}
}

/** Longest any one image may hold up the PDF. */
const IMAGE_TIMEOUT_MS = 15_000

const PRINT_CSS = `
@page { size: ${STAGE_WIDTH}px ${STAGE_HEIGHT}px; margin: 0; }
html, body { margin: 0; padding: 0; background: #ffffff; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`

const imageSettled = (image: HTMLImageElement) =>
	image.complete
		? Promise.resolve()
		: new Promise<void>(resolve => {
				const done = () => resolve()
				image.addEventListener('load', done, { once: true })
				image.addEventListener('error', done, { once: true })
				setTimeout(done, IMAGE_TIMEOUT_MS)
			})

export function PrintDeck({ token }: { token: string }) {
	const [shared, setShared] = useState<SharedPresentation | null>(null)
	const rootRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		window.__PRINT_READY__ = false
		let cancelled = false
		presentationService
			.getPrintPresentation(token)
			.then(data => !cancelled && setShared(data))
			.catch(() => {
				if (!cancelled) window.__PRINT_ERROR__ = 'not_found'
			})
		return () => {
			cancelled = true
		}
	}, [token])

	const slides = useMemo(() => (shared ? presentationSlides(shared.definition, shared.blocks) : []), [shared])
	const assetName = useCallback(
		(ref: EntityRef | null | undefined): AssetName | null => (ref && shared ? shared.assets[`${ref.scope}:${ref.entityId}`] ?? null : null),
		[shared],
	)

	// Chrome writes the page title into the PDF's metadata.
	useEffect(() => {
		if (shared) document.title = `${shared.name} v${shared.version}`
	}, [shared])

	// Drawn: wait for the images and fonts, then say so.
	useEffect(() => {
		if (!shared) return
		let cancelled = false
		const images = [...(rootRef.current?.querySelectorAll('img') ?? [])]
		Promise.all([...images.map(imageSettled), document.fonts?.ready ?? Promise.resolve()])
			// Two frames, so the last layout is painted before the capture.
			.then(() => new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve))))
			.then(() => {
				if (!cancelled) window.__PRINT_READY__ = true
			})
		return () => {
			cancelled = true
		}
	}, [shared, slides])

	if (!shared) return null

	return (
		<div ref={rootRef} data-testid="print-deck">
			<style>{PRINT_CSS}</style>
			{slides.map((entry, index) => (
				<section
					key={`${entry.slide.id}-${entry.continuation?.page ?? 0}`}
					aria-label={`Slide ${index + 1}`}
					// What the PowerPoint export (MYD-512) reads to pair a page with its
					// slide (and its speaker notes, which this page is never sent).
					data-slide-id={entry.slide.id}
					data-divider={entry.divider ? 'true' : undefined}
					data-page={entry.continuation?.page ?? 0}
					style={{
						width: STAGE_WIDTH,
						height: STAGE_HEIGHT,
						overflow: 'hidden',
						// One slide per page, and no blank page after the last.
						breakAfter: index < slides.length - 1 ? 'page' : 'auto',
						pageBreakAfter: index < slides.length - 1 ? 'always' : 'auto',
					}}
				>
					<PresentedSlideView entry={entry} width={STAGE_WIDTH} blocks={shared.blocks} theme={shared.definition.theme} storyCount={shared.definition.stories.length} assetName={assetName} />
				</section>
			))}
		</div>
	)
}

export default PrintDeck
